import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY_PREFIX = 'fileDelivery.savedSearches';

export interface SavedSearch {
  name: string;
  filter?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  isDefault?: boolean;
}

interface UseSavedSearchesReturn {
  savedSearches: SavedSearch[];
  saveSearch: (search: SavedSearch) => { success: boolean; error?: string };
  deleteSearch: (name: string) => void;
  isNameTaken: (name: string) => boolean;
  isLoaded: boolean;
}

const getStorageKey = (listType: string): string => `${STORAGE_KEY_PREFIX}.${listType}`;

const loadSearchesFromStorage = (listType: string): SavedSearch[] => {
  try {
    const stored = localStorage.getItem(getStorageKey(listType));
    if (stored) {
      return JSON.parse(stored) as SavedSearch[];
    }
  } catch {
    console.warn(`Failed to load saved searches for ${listType} from localStorage`);
  }

  return [];
};

const saveSearchesToStorage = (listType: string, searches: SavedSearch[]): void => {
  try {
    localStorage.setItem(getStorageKey(listType), JSON.stringify(searches));
  } catch {
    console.warn(`Failed to save searches for ${listType} to localStorage`);
  }
};

const EMPTY_DEFAULTS: SavedSearch[] = [];

export const useSavedSearches = (
  listType: string,
  defaultSearches: SavedSearch[] = EMPTY_DEFAULTS,
): UseSavedSearchesReturn => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const stored = loadSearchesFromStorage(listType);
    const combined = [...defaultSearches, ...stored];
    setSavedSearches(combined);
    setIsLoaded(true);
  }, [listType, defaultSearches]);

  const isNameTaken = useCallback(
    (name: string): boolean => {
      return savedSearches.some((s) => s.name.toLowerCase() === name.toLowerCase());
    },
    [savedSearches],
  );

  const saveSearch = useCallback(
    (search: SavedSearch): { success: boolean; error?: string } => {
      if (!search.name.trim()) {
        return { success: false, error: 'Name is required' };
      }

      if (isNameTaken(search.name)) {
        return { success: false, error: 'A search with this name already exists' };
      }

      const userSearches = savedSearches.filter((s) => !s.isDefault);
      const newUserSearches = [...userSearches, search];
      saveSearchesToStorage(listType, newUserSearches);

      setSavedSearches([...defaultSearches, ...newUserSearches]);
      return { success: true };
    },
    [savedSearches, listType, defaultSearches, isNameTaken],
  );

  const deleteSearch = useCallback(
    (name: string) => {
      const search = savedSearches.find((s) => s.name === name);
      if (search?.isDefault) return;

      const userSearches = savedSearches.filter((s) => !s.isDefault && s.name !== name);
      saveSearchesToStorage(listType, userSearches);

      setSavedSearches([...defaultSearches, ...userSearches]);
    },
    [savedSearches, listType, defaultSearches],
  );

  return {
    savedSearches,
    saveSearch,
    deleteSearch,
    isNameTaken,
    isLoaded,
  };
};
